import Vue, { PropType } from 'vue';
import { Puzzle } from 'js/crossword/Puzzle';
import { ClueType, CopyrightPlacement, FeedFormat } from 'js/crossword/types';
import { LayoutType } from 'js/crossword/gameSceneLayouts';
import { ISettingsData } from 'js/crossword/settingsData';
import { resizeMainArea } from 'js/crossword/html/resizeMainArea';
import ResizeObserver from 'resize-observer-polyfill';
import { onFreezeChanged, isFrozen } from 'js/main';
import { getCopyrightData } from "../helpers/copyright";
import OS from 'framework/utils/OS';
import Board from "./Board";

Vue.component('puzzle-game-area-regular', {
  props: {
    puzzle: Puzzle,
    settings: Object as PropType<ISettingsData>,
    ui: Object,
  },
  mounted() {
    const gameResizeObserver = new ResizeObserver(() => this.resizeGame());
    gameResizeObserver.observe(this.$refs.boardContainer as HTMLElement);
    this.resizeGame();

    const resizeObserver = new ResizeObserver(() => this.onResize());
    resizeObserver.observe(this.$refs.acrossColumnCell as HTMLElement);
    resizeObserver.observe(this.$refs.downColumnCell as HTMLElement);
    this.onResize();

    resizeObserver.observe(this.$refs.mainArea as HTMLElement);
    this.resizeMainArea();
    onFreezeChanged.add(() => this.resizeMainArea());
    window.addEventListener('resize', () => this.resizeMainArea());
  },
  computed: {
    layoutClass(): string {
      if (OS.desktop) {
        return {
          [LayoutType.RightOneColumn]: 'gameScene__mainArea--oneRight',
          [LayoutType.RightTwoColumns]: 'gameScene__mainArea--twoRight',
          [LayoutType.LeftOneColumn]: 'gameScene__mainArea--oneLeft',
          [LayoutType.LeftTwoColumns]: 'gameScene__mainArea--twoLeft',
        }[this.settings.layout];
      } else {
        return 'gameScene__mainArea--mobile';
      }
    },
  },
  watch: {
    'settings.leftClues': {
      handler() {
        this.resizeMainArea();
      },
    },
  },
  methods: {
    zoomBoardIn() {
      this.$refs.board.zoomIn();
    },
    zoomBoardOut() {
      this.$refs.board.zoomOut();
    },
    resizeMainArea() {
      resizeMainArea({
        mainArea: this.$refs.mainArea as HTMLElement,
        clueBar: this.$refs.clueBar.$el as HTMLElement,
        boardRatio: this.puzzle.getBoardWidth() / this.puzzle.getBoardHeight(),
        settings: this.settings,
        boardColumn: this.$refs.boardColumn as HTMLElement,
        gameScene: document.getElementById('gameScene')!,
        freeze: isFrozen(),
      });
    },
    resizeGame() {
      const containerWidth = (this.$refs.boardContainer as HTMLElement).clientWidth;
      const containerHeight = (this.$refs.boardContainer as HTMLElement).clientHeight;
      const containerRatio = containerWidth / containerHeight;
      const boardRatio = this.puzzle.data.columns / this.puzzle.data.rows;

      if (containerRatio > boardRatio) {
        this.$refs.board.resize(containerHeight * boardRatio, containerHeight);
      } else {
        this.$refs.board.resize(containerWidth, containerWidth / boardRatio);
      }
    },
    onResize() {
      if (!OS.desktop) return;
      this.$refs.acrossColumn.$el.style.height = `${this.$refs.acrossColumnCell!.clientHeight}px`;
      this.$refs.downColumn.$el.style.height = `${this.$refs.downColumnCell!.clientHeight}px`;
    },
    getCellWidth(): number {
      const { columns } = this.puzzle.data;
      const board = this.$refs.board as Vue;

      if (!board || !board.$el) return 0;

      return board.$el.clientWidth / columns;
    },
    getCopyrightConfig() {
      const format = this.puzzle.data.metaData.format;
      const { message, placement } = getCopyrightData(format);
      const offset = format === FeedFormat.LAROUSSE
        ? this.getCellWidth() / 2
        : 0;

      return {
        message,
        placement,
        offset
      }
    }
  },
  render() {
    const copyright = this.getCopyrightConfig();

    return (
      <div id='gameScene__mainArea' class={this.layoutClass} ref='mainArea'>
        <div id='gameScene__acrossColumn' ref='acrossColumnCell'>
          <clue-column puzzle={this.puzzle} ui={this.ui} type={ClueType.Across} ref='acrossColumn'/>
        </div>
        <div id='gameScene__downColumn' ref='downColumnCell'>
          <clue-column puzzle={this.puzzle} ui={this.ui} type={ClueType.Down} ref='downColumn'/>
        </div>
        <div id='gameScene__boardColumn' ref='boardColumn'>
          <current-clue-bar puzzle={this.puzzle} hidden={this.ui.hiddenClues} ref='clueBar'/>
          <div id='gameScene__boardContainer' ref='boardContainer'>
            <Board puzzle={this.puzzle} ref='board'/>
          </div>
          {!OS.desktop && <current-clue-bar puzzle={this.puzzle} hidden={this.ui.hiddenClues} ref='clueBar'/>}
          { copyright.placement === CopyrightPlacement.BOARD 
            ? <div
                class="board--copyright"
                style={{ transform: `translate(${copyright.offset}px, 0)` }}
              >
                { copyright.message }
              </div>
            : null }
        </div>
      </div>
    );
  },
});
